// @ts-nocheck
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search } from "lucide-react";
import { useEventStaff, useStaffAssignments, useUpcomingEventsForStaffing } from "@/hooks/useStaffingData";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

const StaffDirectoryTab: React.FC = () => {
  const { data: staff, isLoading } = useEventStaff();
  const [search, setSearch] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);

  // Fetch all assignments for selected staff member
  const { data: staffAssignments, isLoading: assLoading } = useQuery({
    queryKey: ["staff-all-assignments", selectedStaffId],
    enabled: !!selectedStaffId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manage_staff_event_assignments")
        .select("*, role:manage_staff_roles(*), event:events(id, name, start_date, end_date)")
        .eq("staff_id", selectedStaffId!)
        .order("shift_date", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const filtered = (staff || []).filter((s: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
  });

  const selectedStaff = staff?.find((s: any) => s.id === selectedStaffId);

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} staff members</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No staff found.</TableCell></TableRow>
                ) : filtered.map((s: any) => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedStaffId(s.id)}>
                    <TableCell className="font-medium text-primary underline-offset-2 hover:underline">{s.name}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>{s.phone || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={s.status === "active" || !s.status ? "success" : "secondary"}>
                        {s.status || "active"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Staff detail modal */}
      <Dialog open={!!selectedStaffId} onOpenChange={(o) => { if (!o) setSelectedStaffId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedStaff?.name || "Staff Member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{selectedStaff?.email} • {selectedStaff?.phone || "No phone"}</p>
            <h4 className="font-semibold text-sm mt-4">Upcoming Assignments</h4>
            {assLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : (staffAssignments || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No assignments found.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(staffAssignments || []).map((a: any) => (
                      <TableRow key={a.id}>
                        <TableCell className="font-medium">{a.event?.name || "—"}</TableCell>
                        <TableCell>{a.role?.name || "—"}</TableCell>
                        <TableCell>{a.shift_date ? format(new Date(a.shift_date), "MMM d, yyyy") : "—"}</TableCell>
                        <TableCell><Badge variant="outline">{a.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffDirectoryTab;
